//libraries
import { displayCityName, displayCountryName } from "components/b2c/explore/exploreCommon";
import { t } from "i18n/index";
import { discoverPath } from "i18n/urlLocale";
import { sizedPhoto, srcSetFor } from "utils/photoUrl";

//Components
import Footer from "../components/Footer";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Navigation from "../components/navigation/Navigation";
import { getBlogTranslations } from "../marketing/i18n/pages/blog";
import styles from "./DiscoverHub.module.scss";

// The card grid is three columns from 1024 px, so a card image is about 400 CSS px wide. The
// stored URLs come from the CDN at w=1080 — 17 country photos at ~212 KB each on a page whose
// whole job is to be crawled and clicked through (123yb6937gx).
const CARD_IMG_W = 400;

export interface DiscoverHubDestination {
	slug: string;
	name: string;
}

export interface DiscoverHubCountry {
	slug: string;
	name: string;
	tagline?: string;
	photo: string | null;
	destinations: DiscoverHubDestination[];
}

export interface DiscoverHubInitialData {
	countries?: DiscoverHubCountry[];
}

interface DiscoverHubProps {
	/** Server-side pre-fetched data injected via SSR (`loadDiscoverHub`, marketingSsrLoaders.ts). */
	initialData?: DiscoverHubInitialData | null;
	/**
	 * The page's locale, passed by `MarketingShell.tsx` from the matched route (`langOverride` /
	 * the URL prefix), NOT `i18n.language` — there is no i18next instance in the marketing bundle,
	 * so that resolves to `undefined` during SSR. `DiscoverCountryPage.tsx` carries two long
	 * comments about exactly this trap; the short version is that reading `i18n.language` here
	 * would silently ship English chrome and un-prefixed `/discover/<slug>` links on every
	 * non-English locale of this page.
	 */
	lang?: string;
}

/**
 * `/discover` — the hub page above every country and destination guide.
 *
 * Before 123yb6937h0 this path had no server-rendered page at all: a 200 with the 10,660-byte SPA
 * shell, 0 `<h1>`, 0 `<a href>` — nothing for a crawler to follow down to the ~170 country and
 * destination pages beneath it. This component exists to fix exactly that: a real `<h1>`, and a
 * real `<a href>` for every country AND every destination under it.
 *
 * The ticket describes the old page as still *receiving* equity ("linked from the homepage
 * ... and from the nav search"). That is no longer true and was worth checking rather than
 * repeating: the homepage's two "Browse all"/"All destinations" CTAs were turned into
 * plan-gated buttons while this page was a dead shell (`TrendingDestinationsSection.tsx`,
 * `CommunitySection.tsx`), so the hub had no inbound internal links at all. `Footer.tsx` now
 * carries one site-wide — without it this page would be an orphan with nothing to pass on.
 */
const DiscoverHub = ({ initialData, lang }: DiscoverHubProps = {}) => {
	const currentLang = lang || "en";
	// `getBlogTranslations` is the loader for the whole marketing-pages dictionary, not just the
	// blog — the same file already holds every `guide.*` key the country pages read. The hub's
	// copy lives under `discover.hub.*` there.
	const translations = getBlogTranslations(currentLang);
	const countries = initialData?.countries || [];

	// `t()` is a flat lookup with no interpolation (i18n/index.ts), so the one placeholder this
	// page has gets filled here. Separate singular/plural keys rather than "1 destinations":
	// German and Dutch both inflect the noun, so a shared string with a swapped number is wrong
	// in five of the seven locales.
	// The stored `name` is English on both collections — proper nouns are not in the overlay's
	// translatable allowlist (exploreTranslation.ts). The guide pages this hub links to already
	// localise their own H1 through these two helpers (`DiscoverCountryPage.tsx`,
	// `DiscoverDestinationPage.tsx`), so without them the Dutch hub would offer "Germany" and
	// "Cologne" as the anchor text for pages headed "Duitsland" and "Keulen".
	const countryName = (name: string) => displayCountryName(name, currentLang);
	const cityName = (name: string) => displayCityName(name, currentLang);

	const destinationCount = (count: number) =>
		count === 1
			? t(translations, "discover.hub.destinationCountOne", "1 destination")
			: t(translations, "discover.hub.destinationCountOther", "{count} destinations").replace("{count}", String(count));

	return (
		<div id="stippl-discover-hub" className={styles.page}>
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			<div className={styles.hero}>
				<div className={styles.container}>
					<span className={styles.eyebrow}>{t(translations, "discover.hub.eyebrow", "Destination guides")}</span>
					<h1 className={styles.title}>{t(translations, "discover.hub.title", "Explore every destination")}</h1>
					<p className={styles.subtitle}>
						{t(
							translations,
							"discover.hub.subtitle",
							"Every country and city we've written a full guide for — pick one to start planning."
						)}
					</p>
				</div>
			</div>

			<div className={`${styles.container} ${styles.gridWrap}`}>
				{countries.length === 0 ? (
					<p className={styles.empty}>{t(translations, "discover.hub.empty", "No guides published yet.")}</p>
				) : (
					<div className={styles.grid}>
						{countries.map((country) => {
							const countryHref = discoverPath(`/discover/${country.slug}`, currentLang);
							return (
								<div key={country.slug} className={styles.card}>
									<a href={countryHref} className={styles.cardLink}>
										{country.photo ? (
											<img
												src={sizedPhoto(country.photo, CARD_IMG_W)}
												srcSet={srcSetFor(country.photo, CARD_IMG_W) || undefined}
												alt={countryName(country.name)}
												loading="lazy"
												className={styles.cardImg}
											/>
										) : (
											<div className={styles.cardImgPlaceholder} aria-hidden="true" />
										)}
										<div className={styles.cardBody}>
											<p className={styles.cardName}>{countryName(country.name)}</p>
											{country.tagline && <p className={styles.cardTagline}>{country.tagline}</p>}
											{country.destinations.length > 0 && (
												<p className={styles.cardCount}>{destinationCount(country.destinations.length)}</p>
											)}
										</div>
									</a>

									{country.destinations.length > 0 && (
										<ul className={styles.destList}>
											{country.destinations.map((destination) => (
												<li key={destination.slug}>
													<a
														href={discoverPath(`/discover/${country.slug}/${destination.slug}`, currentLang)}
														className={styles.destLink}
													>
														{cityName(destination.name)}
													</a>
												</li>
											))}
										</ul>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>

			<Footer />
		</div>
	);
};

export default DiscoverHub;
